import { useEffect, useState } from "react";
import { FaMapMarkerAlt, FaChrome, FaFirefox, FaSafari, FaEdge, FaGlobe } from "react-icons/fa";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdLocationOff, MdMyLocation } from "react-icons/md";

/**
 * Detects the user's browser based on userAgent
 * @returns {'chrome' | 'firefox' | 'safari' | 'edge' | 'other'}
 */
const detectBrowser = () => {
	const userAgent = navigator.userAgent.toLowerCase();

	if (userAgent.includes("edg/")) return "edge";
	if (userAgent.includes("chrome") && !userAgent.includes("edg/")) return "chrome";
	if (userAgent.includes("firefox")) return "firefox";
	if (userAgent.includes("safari") && !userAgent.includes("chrome")) return "safari";

	return "other";
};

/**
 * Browser-specific instructions for enabling location
 */
const browserInstructions = {
	chrome: {
		name: "Chrome",
		icon: FaChrome,
		steps: [
			"Click the lock/tune icon in the address bar (left of the URL)",
			"Click 'Site settings'",
			"Find 'Location' and change it to 'Allow'",
			"Refresh the page",
		],
	},
	firefox: {
		name: "Firefox",
		icon: FaFirefox,
		steps: [
			"Click the shield icon in the address bar",
			"Click the gear icon next to 'Enhanced Tracking Protection'",
			"In Permissions, find 'Access Your Location' and select 'Allow'",
			"Refresh the page",
		],
	},
	safari: {
		name: "Safari",
		icon: FaSafari,
		steps: [
			"Open Safari menu → Settings (or Preferences)",
			"Click 'Websites' tab",
			"Select 'Location' from the left sidebar",
			"Find this website and change permission to 'Allow'",
		],
	},
	edge: {
		name: "Edge",
		icon: FaEdge,
		steps: [
			"Click the lock icon in the address bar",
			"Click 'Permissions for this site'",
			"Find 'Location' and change it to 'Allow'",
			"Refresh the page",
		],
	},
	other: {
		name: "your browser",
		icon: FaGlobe,
		steps: [
			"Look for a location or permissions icon in your address bar",
			"Check your browser settings for site permissions",
			"Enable location access for this website",
			"Refresh the page",
		],
	},
};

/**
 * LocationPermissionHelper - Shows contextual guidance for enabling location services
 *
 * @param {Object} props
 * @param {boolean} props.compact - Show a compact version of the helper
 * @param {string} props.className - Additional CSS classes
 */
const LocationPermissionHelper = ({ compact = false, className = "" }) => {
	const [permissionStatus, setPermissionStatus] = useState("checking");
	const [showInstructions, setShowInstructions] = useState(false);
	const [browser] = useState(() => detectBrowser());

	useEffect(() => {
		// Check if Permissions API is supported
		if (!navigator.permissions) {
			setPermissionStatus("unsupported");
			return;
		}

		navigator.permissions
			.query({ name: "geolocation" })
			.then((result) => {
				setPermissionStatus(result.state);

				// Listen for permission changes
				result.onchange = () => {
					setPermissionStatus(result.state);
				};
			})
			.catch(() => {
				setPermissionStatus("unsupported");
			});
	}, []);

	const browserInfo = browserInstructions[browser];
	const BrowserIcon = browserInfo.icon;

	// Don't show anything while checking
	if (permissionStatus === "checking") {
		return null;
	}

	// Permission granted - show brief success or nothing
	if (permissionStatus === "granted") {
		if (compact) return null;

		return (
			<div className={`p-3 rounded-lg border bg-green-50 border-green-200 ${className}`}>
				<div className="flex items-center gap-2 text-green-700">
					<AiOutlineCheckCircle className="w-5 h-5 flex-shrink-0" />
					<span className="text-sm">Location access enabled - you can use "Pick on map"</span>
				</div>
			</div>
		);
	}

	// Permission denied - show warning with instructions
	if (permissionStatus === "denied") {
		return (
			<div className={`p-4 rounded-lg border bg-amber-50 border-amber-200 ${className}`}>
				<div className="flex items-start gap-3">
					<MdLocationOff className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
					<div className="flex-1">
						<p className="text-sm font-medium text-amber-800">
							Location access is blocked
						</p>
						<p className="text-sm text-amber-700 mt-1">
							To use "Pick on map" for easier address selection, please enable location access for this site.
						</p>

						<button
							type="button"
							onClick={() => setShowInstructions(!showInstructions)}
							className="mt-2 text-sm font-medium text-amber-800 underline hover:text-amber-900 flex items-center gap-1"
						>
							<BrowserIcon className="w-4 h-4" />
							{showInstructions ? "Hide" : "Show"} instructions for {browserInfo.name}
						</button>

						{showInstructions && (
							<div className="mt-3 p-3 bg-white rounded-md border border-amber-200">
								<p className="text-xs font-medium text-gray-700 mb-2">
									To enable location in {browserInfo.name}:
								</p>
								<ol className="text-xs text-gray-600 space-y-1.5 list-decimal list-inside">
									{browserInfo.steps.map((step, index) => (
										<li key={index}>{step}</li>
									))}
								</ol>
							</div>
						)}

						<p className="text-xs text-amber-600 mt-3">
							You can still enter your address manually using the search field.
						</p>
					</div>
				</div>
			</div>
		);
	}

	// Permission prompt (not yet asked) - show info banner
	return (
		<div className={`p-3 rounded-lg border bg-blue-50 border-blue-200 ${className}`}>
			<div className="flex items-start gap-3">
				<FaMapMarkerAlt className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
				<div className="flex-1">
					<p className="text-sm font-medium text-blue-800">
						Enable location for faster address entry
					</p>
					<p className="text-sm text-blue-700 mt-1">
						When you click "Pick on map", your browser will ask for location permission.
						Allowing access lets us show your current location on the map for easier address selection.
					</p>
					{!compact && (
						<div className="flex items-center gap-2 mt-2 text-xs text-blue-600">
							<MdMyLocation className="w-4 h-4" />
							<span>Your location is only used for delivery address selection</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default LocationPermissionHelper;
